import { useContext, useEffect, useRef, useState } from 'react'
import { Header, LoadingSpinner, TextInput } from '@components/Generic'
import { observer } from 'mobx-react'
import { axiosInstance } from '@methods/axiosConfig'
import { forceRedirect } from '@methods/forceRedirect'
import { devLog } from '@methods/devLog'

import { StoreContext } from '@store/Root.store'
import { onKeyDownHandler } from '@/methods/keyboardAccessibility'
import { getUrlParam } from '@/methods/urlParams'

interface Institution {
  bic?: string
  countries?: string[]
  id: string
  logo?: string
  name?: string
  transactionTotalDays?: number
}

interface BankData {
  country: string
  institutions: Institution[]
}

export const GocardlessBankSelectionPage = observer(() => {
  const store = useContext(StoreContext)
  const { gocardlessBankSelectionPage: trans } = store.TranslationsState.translations
  const selectedCountry = getUrlParam('selectedCountry')

  const [bankList, setBankList] = useState<Institution[]>([])
  const [search, setSearch] = useState('')
  const [redirecting, setRedirecting] = useState(false)
  const [initialized, setInitialized] = useState(false)

  const abortControllerRef = useRef<AbortController | null>(null)

  const initGocardless = async (bank: string) => {
    setRedirecting(true)

    try {
      const { data } = await axiosInstance.post<{ redirectUrl: string }>(
        '/gocardless/start-session',
        { institutionId: bank },
        {
          withCredentials: true,
        }
      )

      forceRedirect(data.redirectUrl)
    } catch (error) {
      devLog('error: ', error)
    }
  }

  const goBackHandler = () => {
    setSearch('')
    store.ScenarioState.prevPage()
  }

  const fetchBankList = async (searchTerm?: string) => {
    abortControllerRef.current?.abort()
    const controller = new AbortController()
    abortControllerRef.current = controller

    try {
      const { data } = await axiosInstance.get<BankData>('/gocardless/list-institutions', {
        withCredentials: true,
        signal: controller.signal,
        params: {
          country: selectedCountry,
          ...(searchTerm ? { search: searchTerm } : {}),
        },
      })
      setBankList(data.institutions)
    } catch (error) {
      if (!controller.signal.aborted) {
        devLog(error)
      }
    }
  }

  useEffect(() => {
    if (!selectedCountry) return

    void fetchBankList().then(() => setInitialized(true))
  }, [selectedCountry])

  useEffect(() => {
    if (!initialized) return

    const timeout = setTimeout(() => {
      void fetchBankList(search || undefined)
    }, 500)

    return () => clearTimeout(timeout)
  }, [search])

  return (
    <div className="page-container mobile-m-0">
      {redirecting ? (
        <>
          <Header>{trans.redirecting}</Header>
          <>
            <LoadingSpinner />

            <div className="layout__centered-content m-20-0-0-0">{trans.pleaseWait}...</div>
          </>
        </>
      ) : (
        <>
          <button className="generic__back-button" onClick={goBackHandler}>
            <svg
              aria-hidden="true"
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M19.4595 21.4595C19.2057 21.7134 18.7941 21.7134 18.5403 21.4595L13.5403 16.4595C13.2864 16.2057 13.2864 15.7941 13.5403 15.5403L18.5403 10.5403C18.7941 10.2864 19.2057 10.2864 19.4595 10.5403C19.7134 10.7941 19.7134 11.2057 19.4595 11.4595L14.9191 15.9999L19.4595 20.5403C19.7134 20.7941 19.7134 21.2057 19.4595 21.4595Z"
                fill="black"
              />
            </svg>
            {trans.back}
          </button>
          <Header>{trans.header}</Header>

          <div className="generic__country-wrap">
            {!initialized ? (
              <>
                <LoadingSpinner />
                <div className="layout__centered-content m-20-0-0-0">{trans.banksListLoading}</div>
              </>
            ) : (
              <>
                <div className="generic__input-container">
                  <label className="generic__label" htmlFor="bank-search-input">
                    {trans.inputLabel}
                  </label>
                  <TextInput
                    type="text"
                    value={search}
                    onChange={(value) => {
                      setSearch(value)
                    }}
                    placeholder={trans.bankSearchPlaceholder}
                    id="bank-search-input"
                  />
                </div>

                <h2 className="generic__banks-list-label" data-testid="chooseDocument">
                  {trans.banksIn}
                </h2>
                {bankList.length ? (
                  <div className="generic__bank-badge-list">
                    {bankList.map((bank) => (
                      <div
                        className="generic__bank-badge"
                        key={bank.id}
                        onClick={() => initGocardless(bank.id)}
                        tabIndex={0}
                        role="button"
                        onKeyDown={(event) =>
                          onKeyDownHandler(event, () => initGocardless(bank.id))
                        }
                      >
                        <div className="generic__bank-badge-icon">
                          <img src={bank.logo} alt={bank.name} />
                        </div>
                        <div className="generic__bank-badge-name">{bank.name}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="generic__bank-badge-list-empty">
                    <div>
                      <svg
                        aria-hidden="true"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M9.99967 3.04183C6.15669 3.04183 3.04134 6.15718 3.04134 10.0002C3.04134 13.8431 6.15669 16.9585 9.99967 16.9585C13.8427 16.9585 16.958 13.8431 16.958 10.0002C16.958 6.15718 13.8427 3.04183 9.99967 3.04183ZM1.95801 10.0002C1.95801 5.55887 5.55838 1.9585 9.99967 1.9585C14.441 1.9585 18.0413 5.55887 18.0413 10.0002C18.0413 14.4415 14.441 18.0418 9.99967 18.0418C5.55838 18.0418 1.95801 14.4415 1.95801 10.0002Z"
                          fill="currentColor"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M9.99967 5.29183C10.2988 5.29183 10.5413 5.53434 10.5413 5.8335V10.8335C10.5413 11.1326 10.2988 11.3752 9.99967 11.3752C9.70052 11.3752 9.45801 11.1327 9.45801 10.8335V5.8335C9.45801 5.53434 9.70052 5.29183 9.99967 5.29183Z"
                          fill="currentColor"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M10.6247 13.5418C10.6247 13.887 10.3449 14.1668 9.99967 14.1668C9.6545 14.1668 9.37467 13.887 9.37467 13.5418C9.37467 13.1967 9.6545 12.9168 9.99967 12.9168C10.3449 12.9168 10.6247 13.1967 10.6247 13.5418Z"
                          fill="currentColor"
                        />
                      </svg>
                    </div>
                    <div>{trans.bankSearchError}</div>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  )
})
